import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { getToken, isLoggedIn, logout } from "../services/authClient";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [sessionWarning, setSessionWarning] = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);

  const base64UrlDecode = useCallback((input) => {
    try {
      let base64 = input.replace(/-/g, "+").replace(/_/g, "/");
      const pad = base64.length % 4;
      if (pad) base64 += "=".repeat(4 - pad);
      return atob(base64);
    } catch {
      return null;
    }
  }, []);

  const parseJwt = useCallback(
    (token) => {
      try {
        const payloadPart = token.split(".")[1];
        const json = base64UrlDecode(payloadPart);
        if (!json) return null;
        return JSON.parse(json);
      } catch {
        return null;
      }
    },
    [base64UrlDecode]
  );

  const [authState, setAuthState] = useState(() => {
    // Initialize from localStorage
    const token = getToken();
    const isAuthenticated = isLoggedIn();

    if (isAuthenticated && token) {
      // Decode token immediately on init
      const decodedToken = (() => {
        try {
          const payloadPart = token.split(".")[1];
          const json = (payloadPart && atob(payloadPart)) || null;
          return json ? JSON.parse(json) : null;
        } catch {
          return null;
        }
      })();

      if (decodedToken) {
        return {
          isAuthenticated: true,
          token,
          userRole: decodedToken.role || null,
        };
      }
      // Invalid token, clear it
      logout();
      return {
        isAuthenticated: false,
        token: null,
        userRole: null,
      };
    }

    return {
      isAuthenticated: false,
      token: null,
      userRole: null,
    };
  });

  // Function to decode JWT and get user role and expiration
  const decodeToken = useCallback(
    (token) => {
      if (!token) return null;
      const decodedToken = parseJwt(token);
      if (!decodedToken) return null;
      return {
        role: decodedToken.role,
        exp: decodedToken.exp ? decodedToken.exp * 1000 : undefined,
      };
    },
    [parseJwt]
  );

  // Immediately apply a freshly received token to context
  const applyToken = useCallback(
    (token) => {
      if (!token) {
        setAuthState({ isAuthenticated: false, token: null, userRole: null });
        return;
      }
      const tokenData = decodeToken(token);
      if (tokenData && tokenData.role) {
        setAuthState({ isAuthenticated: true, token, userRole: tokenData.role });
      } else {
        setAuthState({ isAuthenticated: false, token: null, userRole: null });
      }
    },
    [decodeToken]
  );

  // Function to refresh auth state from localStorage
  const refreshAuthState = useCallback(() => {
    const token = getToken();
    const isAuthenticated = isLoggedIn();

    if (process.env.NODE_ENV === "development") {
      console.log("AuthContext: Refreshing auth state", {
        isAuthenticated,
        hasToken: !!token,
      });
    }

    if (isAuthenticated && token) {
      const tokenData = decodeToken(token);
      if (tokenData && tokenData.role) {
        setAuthState({
          isAuthenticated: true,
          token,
          userRole: tokenData.role,
        });

        if (process.env.NODE_ENV === "development") {
          console.log("AuthContext: Auth state updated", {
            userRole: tokenData.role,
          });
        }
      } else {
        // Invalid token
        logout();
        setAuthState({
          isAuthenticated: false,
          token: null,
          userRole: null,
        });
      }
    } else {
      setAuthState({
        isAuthenticated: false,
        token: null,
        userRole: null,
      });
    }
  }, [decodeToken]);

  // Listen for localStorage changes (for when login happens)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "token") {
        if (process.env.NODE_ENV === "development") {
          console.log("AuthContext: Token changed in localStorage");
        }
        refreshAuthState();
      }
    };

    // Listen for storage events from other tabs/windows
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [refreshAuthState]);

  // Periodically check localStorage for token changes (for same-tab changes)
  useEffect(() => {
    const checkTokenChanges = () => {
      const currentToken = getToken();
      const currentAuth = isLoggedIn();

      // Check if the localStorage state differs from our React state
      if (
        (currentAuth && !authState.isAuthenticated) ||
        (!currentAuth && authState.isAuthenticated) ||
        currentToken !== authState.token
      ) {
        if (process.env.NODE_ENV === "development") {
          console.log("AuthContext: Detected auth state mismatch, refreshing");
        }
        refreshAuthState();
      }
    };

    // Initial check after mount
    const timeout = setTimeout(checkTokenChanges, 500);

    // Then check periodically
    const interval = setInterval(checkTokenChanges, 3000); // Check every 3 seconds

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [authState.isAuthenticated, authState.token, refreshAuthState]);

  // Manual logout function
  const handleLogout = useCallback(() => {
    logout();
    setAuthState({ isAuthenticated: false, token: null, userRole: null });
    setSessionWarning(false);
    setSessionTimeLeft(null);
  }, []);

  // Function to dismiss session warning
  const extendSession = useCallback(() => {
    setSessionWarning(false);
    setSessionTimeLeft(null);
  }, []);

  // Session monitoring - only runs if authenticated
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.token) return;

    const tokenData = decodeToken(authState.token);
    if (!tokenData || !tokenData.exp) return;

    const checkSession = () => {
      const timeLeft = tokenData.exp - Date.now();

      // If token is expired, logout immediately
      if (timeLeft <= 0) {
        if (process.env.NODE_ENV === "development") {
          console.log("Token expired - auto logout");
        }
        handleLogout();
        return;
      }

      // Show warning 5 minutes (300000ms) before expiration
      const warningThreshold = 5 * 60 * 1000; // 5 minutes

      if (timeLeft <= warningThreshold && !sessionWarning) {
        setSessionWarning(true);
        setSessionTimeLeft(Math.ceil(timeLeft / 1000 / 60)); // Convert to minutes
      } else if (sessionWarning) {
        // Update time left if warning is showing
        setSessionTimeLeft(Math.ceil(timeLeft / 1000 / 60));
      }
    };

    // Initial check
    checkSession();

    // Set up interval to check every minute
    const interval = setInterval(checkSession, 60000);

    return () => clearInterval(interval);
  }, [
    authState.isAuthenticated,
    authState.token,
    sessionWarning,
    decodeToken,
    handleLogout,
  ]);

  const contextValue = {
    auth: authState, // Use the internal authState
    refreshAuthState, // Expose refresh function instead of setAuth
    applyToken,
    handleLogout,
    extendSession,
    sessionWarning,
    sessionTimeLeft,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuthContext = () => useContext(AuthContext);
